import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sun, Moon, Maximize2, AlertCircle } from 'lucide-react';
import { ensureProtocolForCourse, renderProtocolHtml } from '../lib/course-protocols';
import type { CourseProtocol } from '../types/course-protocols';

interface ProtocoloBimModalProps {
  open:     boolean;
  onClose:  () => void;
  /** Curso del alumno — si no se pasa, el modal muestra el template sin datos. */
  courseId?: string | null;
}

type Theme = 'dark' | 'light';
const STORAGE_KEY = 'protocolo-bim-theme';

/**
 * Base origin para los assets del protocolo.
 *
 * En campus.cubuslatam.com hay un Apache (BlueHosting) que devuelve 500 para
 * cualquier path que no estaba contemplado en su proxy hacia Vercel — eso
 * incluye `/protocolo-bim/`, `/atajos-revit/` y `/api/`. Para esos casos
 * forzamos los assets desde cubus-intranet.vercel.app (mismo deploy, sin
 * Apache en el medio).
 *
 * En dev (localhost) usamos paths relativos para que Vite los sirva.
 */
const ASSETS_ORIGIN = (() => {
  if (typeof window === 'undefined') return '';
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) return '';
  if (h.endsWith('vercel.app')) return ''; // ya estamos en vercel, sin prefijo
  return 'https://cubus-intranet.vercel.app';
})();

const URL_BY_THEME: Record<Theme, string> = {
  dark:  `${ASSETS_ORIGIN}/protocolo-bim/protocolo-cubus-dark.html`,
  light: `${ASSETS_ORIGIN}/protocolo-bim/protocolo-blanco.html`,
};

/**
 * Modal full-screen para el Protocolo BIM CUBUS.
 *
 * Recibe un `courseId`:
 *   - Carga (o crea con auto-seed) el `course_protocols` del curso.
 *   - Hace fetch del HTML template (dark/blanco) desde /public.
 *   - Reemplaza placeholders {{KEY}} con los valores del protocolo.
 *   - Pasa el HTML resultante al iframe via Blob URL (más confiable que
 *     srcDoc en algunos browsers + sandbox).
 *
 * Toggle dark/light recordado en localStorage.
 */
export function ProtocoloBimModal({ open, onClose, courseId }: ProtocoloBimModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'dark';
    return (localStorage.getItem(STORAGE_KEY) as Theme) || 'dark';
  });
  const [protocol, setProtocol] = useState<CourseProtocol | null>(null);
  const [blobUrl, setBlobUrl]   = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);

  // Cerrar con Escape + lock scroll de fondo
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Persistir theme
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Cargar el protocolo del curso (una vez al abrir o al cambiar curso)
  useEffect(() => {
    if (!open) return;
    if (!courseId) { setProtocol(null); return; }
    (async () => {
      const p = await ensureProtocolForCourse(courseId);
      setProtocol(p);
    })();
  }, [open, courseId]);

  // Cargar el HTML del template y reemplazar placeholders cuando cambian
  // theme o protocol
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        // Cache-bust: el HTML del template se actualiza con scripts Python
        // que parametrizan placeholders; un cache viejo del browser puede
        // dejar fechas literales {{FECHA_E1}} que no matchean con valores.
        // `cache: 'no-store'` + query string fuerzan re-fetch siempre.
        const url = `${URL_BY_THEME[theme]}?t=${Date.now()}`;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        if (cancelled) return;

        const rendered = renderProtocolHtml(html, protocol);
        // Blob URL para evitar problemas de same-origin con srcDoc + sandbox
        const blob = new Blob([rendered], { type: 'text/html;charset=utf-8' });
        const objUrl = URL.createObjectURL(blob);
        setBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objUrl;
        });
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'No se pudo cargar el protocolo');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, theme, protocol]);

  // Cleanup del blob al desmontar
  useEffect(() => () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Protocolo BIM"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
    >
      {/* Header / toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08] bg-cubus-deep/80 backdrop-blur flex-shrink-0">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[2px] uppercase font-semibold text-cubus-lime mb-0.5">
            Documento
          </div>
          <h2 className="text-sm font-medium text-white truncate max-w-[60vw]">
            Protocolo BIM CUBUS
            {protocol?.numero_diplomado && (
              <span className="text-white/40 ml-2">· DIP {protocol.numero_diplomado}</span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Toggle claro/oscuro */}
          <div className="inline-flex rounded-lg border border-white/10 bg-white/[0.04] p-1">
            <button
              type="button"
              onClick={() => setTheme('dark')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                theme === 'dark' ? 'bg-cubus-lime text-cubus-bg shadow' : 'text-white/60 hover:text-white'
              }`}
              title="Versión oscura"
              aria-pressed={theme === 'dark'}
            >
              <Moon className="w-3.5 h-3.5" />
              Oscuro
            </button>
            <button
              type="button"
              onClick={() => setTheme('light')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                theme === 'light' ? 'bg-white text-cubus-bg shadow' : 'text-white/60 hover:text-white'
              }`}
              title="Versión clara"
              aria-pressed={theme === 'light'}
            >
              <Sun className="w-3.5 h-3.5" />
              Claro
            </button>
          </div>

          <a
            href={URL_BY_THEME[theme]}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 hover:border-cubus-lime/40 text-white/80 transition-all"
            title="Abrir template sin datos en nueva pestaña"
            aria-label="Abrir en nueva pestaña"
          >
            <Maximize2 className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Nueva pestaña</span>
          </a>
          <button
            onClick={onClose}
            className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 hover:border-cubus-lime/40 text-white/80 transition-all focus-lime"
            title="Cerrar (Esc)"
            aria-label="Cerrar"
          >
            <span className="text-sm">Cerrar</span>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mensaje si el curso no tiene protocolo configurado todavía */}
      {!courseId && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm">
          <AlertCircle className="w-4 h-4" />
          No tenés curso activo. Mostrando el template sin datos.
        </div>
      )}

      {error && (
        <div className="flex-1 flex items-center justify-center text-rose-400">{error}</div>
      )}

      {loading && !blobUrl && (
        <div className="flex-1 flex items-center justify-center text-white/60 text-sm">
          Cargando protocolo…
        </div>
      )}

      {blobUrl && (
        <iframe
          ref={iframeRef}
          src={blobUrl}
          title="Protocolo BIM"
          tabIndex={0}
          onLoad={() => {
            try { iframeRef.current?.contentWindow?.focus(); }
            catch { iframeRef.current?.focus(); }
          }}
          className={`flex-1 w-full border-0 block ${theme === 'dark' ? 'bg-cubus-bg' : 'bg-white'}`}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      )}
    </div>,
    document.body,
  );
}
